import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Phone, Sparkles, HeartPulse, Shield, FileAudio, Search } from 'lucide-react';
import * as Icons from 'lucide-react';
import PageHero from '../components/PageHero';
import CtaBanner from '../components/CtaBanner';
import FaqAccordion from '../components/FaqAccordion';
import Seo from '../components/Seo';
import { OUTBOUND_FEATURES } from '../data/mock';

const OUT_FAQ = [
  { q: 'Will predictive dialing actually double agent productivity?', a: 'Most teams see 1.8\u20132.5x talk-time gains within the first 30 days, depending on list quality, AMD accuracy and agent count.' },
  { q: 'Are you compliant with TPS / TCPA / DNC rules?', a: 'Yes. We support TPS scrubbing, TCPA / DNC list checks, time-window calling, consent tracking and recording prompts.' },
  { q: 'Can we use our own SIP trunks?', a: 'You can. We support BYO trunks or you can use VoIPinger’s wholesale routes for the lowest per-minute cost.' },
  { q: 'Do you support multiple campaigns at once?', a: 'Yes. Run unlimited concurrent campaigns with separate quotas, dial rules and dispositions per campaign.' },
  { q: 'Can we PCI-mask payment portions of a call?', a: 'Yes. PCI-DSS pause / resume is built in — the agent triggers a pause when card details are read out, and the recording resumes automatically afterwards. DTMF digits are masked and an audit log is generated.' },
  { q: 'How do we audit call quality at scale?', a: "Conversational Transcription (Google / Amazon) auto-transcribes every recorded call and splits it per speaker. Sentiment Analysis tags each segment green / amber / red so QA can prioritise the red ones — no more sampling. Add Call Trace on top to see jitter and MOS for every call." },
  { q: 'How do you handle compliance recording prompts?', a: 'You can enable a configurable "this call may be recorded" announcement per campaign, per region or globally. The system also supports DNC / TPS list scrubbing, consent capture and time-window enforcement.' }
];

export default function OutboundContactCentre() {
  return (
    <>
      <Seo
        title="Outbound Contact Centre | Predictive Dialer + QA | VoIPinger"
        description="AI-driven predictive dialer, accurate AMD and built-in QA: Sentiment Analysis, PCI pause/resume, Call Trace + MOS scoring on every campaign call."
        path="/outbound-contact-centre"
        image="https://voipinger.com/assets/heroes/outbound-contact-centre.png"
        service={{ name: 'Outbound Contact Centre', serviceType: 'Outbound dialer / sales contact centre' }}
        faq={OUT_FAQ}
        breadcrumbs={[{ name: 'Home', path: '/' }, { name: 'Outbound Contact Centre', path: '/outbound-contact-centre' }]}
      />
      <PageHero
        eyebrow="Outbound Contact Centre"
        title="Power up outbound campaigns"
        subtitle="AI-driven predictive dialer, accurate AMD, real-time campaign manager and built-in compliance — give your sales team superpowers."
        breadcrumb="Outbound Contact Centre"
      />

      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow">Outbound essentials</span>
            <h2 className="section-title mt-4">A dialer that keeps agents talking</h2>
            <p className="section-sub mt-3">From small sales pods to enterprise BPOs, our outbound suite is purpose-built for high-volume campaigns.</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {OUTBOUND_FEATURES.map((f) => {
              const Ic = Icons[f.icon] || Icons.Sparkles;
              return (
                <div key={f.t} className="card-modern">
                  <div className="w-11 h-11 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center mb-3"><Ic size={20} /></div>
                  <h3 className="font-bold text-slate-900 mb-1">{f.t}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 border-y border-slate-100">
        <div className="container-x section-pad grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="eyebrow">Modes of dialing</span>
            <h2 className="section-title mt-4">Pick the mode for your team</h2>
            <div className="mt-6 space-y-4">
              {[
                { t: 'Predictive', d: 'Dial multiple lines per agent based on real-time stats. Best for high-volume B2C.' },
                { t: 'Power', d: 'A fixed number of calls per agent. Balanced productivity and abandon rate.' },
                { t: 'Progressive', d: 'One call per agent at a time — zero abandon. Great for B2B and regulated calls.' },
                { t: 'Preview', d: 'Agents see contact details before dialing. Ideal for complex sales.' }
              ].map((m) => (
                <div key={m.t} className="flex items-start gap-4 bg-white rounded-xl border border-slate-200 p-4">
                  <span className="w-9 h-9 rounded-lg bg-violet-50 text-violet-600 flex items-center justify-center font-bold">{m.t[0]}</span>
                  <div>
                    <p className="font-semibold text-slate-900">{m.t} dialing</p>
                    <p className="text-sm text-slate-600">{m.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { v: '2.4x', l: 'Avg. talk-time uplift' },
              { v: '< 3%', l: 'Abandon rate' },
              { v: '> 90%', l: 'AMD accuracy' },
              { v: '70%', l: 'Cost per acquisition cut' }
            ].map((s) => (
              <div key={s.l} className="rounded-2xl bg-white border border-slate-200 p-5 text-center">
                <p className="text-3xl font-extrabold gradient-text">{s.v}</p>
                <p className="text-xs text-slate-500 mt-1">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================================== */}
      {/* QA + COMPLIANCE: Sentiment + Recording + Call Trace               */}
      {/* ============================================================== */}
      <section className="bg-white">
        <div className="container-x section-pad">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <span className="eyebrow"><Sparkles size={11} /> QA & Compliance</span>
            <h2 className="section-title mt-4">Coach harder. Audit faster. Stay compliant.</h2>
            <p className="section-sub mt-3">
              Outbound campaigns live or die on quality. Three platform-native tools turn every recorded
              call into searchable, audit-ready evidence.
            </p>
            <div className="divider-svg mt-6" />
          </div>

          <div className="grid md:grid-cols-3 gap-5 max-w-5xl mx-auto">
            <div className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-300 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-rose-500 to-fuchsia-500 text-white flex items-center justify-center mb-4 shadow-md shadow-rose-500/20">
                <HeartPulse size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Sentiment Analysis</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Every transcript segment is auto-tagged <span className="text-emerald-600 font-semibold">green</span> /
                <span className="text-amber-600 font-semibold"> amber</span> /
                <span className="text-rose-600 font-semibold"> red</span>. QA reviews the red flags first —
                lift quality without listening to hours of audio.
              </p>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-300 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-indigo-500 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/20">
                <Shield size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">PCI Pause / Resume</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Take card payments on a recorded line without leaking PAN data — pause the recording
                while card details are read, automatic resume afterwards, DTMF masking and audit logs.
              </p>
            </div>

            <div className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-violet-300 hover:shadow-md transition-all">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-sky-500 to-cyan-500 text-white flex items-center justify-center mb-4 shadow-md shadow-sky-500/20">
                <Search size={22} />
              </div>
              <h3 className="font-extrabold text-slate-900 mb-2">Call Trace + MOS</h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                Full SIP ladder, PCAP and MOS quality score on every call. Diagnose jitter and packet
                loss after the fact instead of trying to re-create faults — and protect campaign ROI.
              </p>
            </div>
          </div>

          <div className="max-w-3xl mx-auto mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/contact" className="btn-primary">See a QA workflow in action <ArrowRight size={14} /></Link>
            <Link to="/phone-features" className="btn-ghost">All features</Link>
          </div>
        </div>
      </section>

      <FaqAccordion items={OUT_FAQ} title="Outbound Contact Centre FAQ" />
      <CtaBanner title="Double your outbound productivity" subtitle="Free 14-day pilot — we provision the dialer, you bring the list." />
    </>
  );
}
